import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "./Modal"; // Relies on LaunchSafely modal overlay in ./Modal.tsx
import type { RunResultResponse, RunTest } from "./runContracts";
import { getRunResult, getRunStatus, RunApiError } from "./runApi";
import { useTheme } from "./theme";
import { resolveApiBase } from "./config";

interface ResultsProps {
  show: boolean;
  demoTargetUrl?: string;
  runId?: string;
  onHireRequest?: () => void;
}

export default function Results({
  show,
  demoTargetUrl,
  runId,
  onHireRequest,
}: ResultsProps) {
  const [showReport, setShowReport] = useState(false);
  const [loading, setLoading] = useState<boolean>(!!runId);
  const [statusNote, setStatusNote] = useState<string>("Initializing...");
  const [liveData, setLiveData] = useState<RunResultResponse | null>(null);
  const pollRef = useRef<any>(null);
  const [progressPct, setProgressPct] = useState(0);
  const [usingDemoFallback, setUsingDemoFallback] = useState<boolean>(!runId);
  const { theme } = useTheme();
  const palette = theme.palette;
  const fallbackCallout =
    theme.mode === "dark"
      ? {
          background: "rgba(251, 146, 60, 0.15)",
          border: "1px solid rgba(251, 146, 60, 0.35)",
          color: "#fcdab0",
        }
      : {
          background: "rgba(255, 196, 115, 0.18)",
          border: "1px solid rgba(255, 196, 115, 0.45)",
          color: "#b45309",
        };

  const apiBase = useMemo(() => resolveApiBase(), []);

  const runInsights = useMemo(
    () => ({
      coverage: 83,
      totals: {
        total: 11,
        passed: 7,
        failed: 2,
        warnings: 1,
        info: 1,
        primary: { total: 8, passed: 6, failed: 2, info: 0 },
        secondary: { total: 3, passed: 1, warnings: 1, info: 1 },
      },
      suiteName: "Staging Smoke Suite",
      tool: "LaunchSafely Cloud Runner (Playwright)",
      triggeredBy: "Scheduled nightly run",
      executedAt: "Apr 10, 2025 | 02:18 UTC",
      duration: "6m 42s",
      tests: [
        {
          id: "demo-home-status",
          name: "Homepage responded with HTTP 200",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "Homepage navigation returned HTTP 200.",
        },
        {
          id: "demo-title",
          name: "Document title rendered",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "Document title provided meaningful copy.",
        },
        {
          id: "demo-meta-description",
          name: "Meta description present",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "Meta description tag found.",
        },
        {
          id: "demo-hero-headline",
          name: "Hero headline text found",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "Hero headline visible above the fold.",
        },
        {
          id: "demo-cta-visible",
          name: "Primary call-to-action visible",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "Primary CTA visible within initial viewport.",
        },
        {
          id: "demo-hero-image",
          name: "Hero image includes descriptive alt text",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "Hero image shipped with descriptive alt text.",
        },
        {
          id: "demo-cta-destination",
          name: "Primary CTA opens expected destination",
          status: "failed",
          passed: false,
          category: "primary",
          summary: "Clicked CTA but no navigation or modal opened.",
          recommendation: "Verify the CTA link target or handler binding.",
        },
        {
          id: "demo-auth-guard",
          name: "Authenticated page enforces session guard",
          status: "failed",
          passed: false,
          category: "primary",
          summary: "Visited /dashboard unauthenticated and saw content.",
          recommendation:
            "Require auth middleware before serving dashboard routes.",
        },
        {
          id: "demo-first-party-errors",
          name: "No 4xx/5xx responses for first-party requests",
          status: "passed",
          passed: true,
          category: "primary",
          summary: "No failing first-party API calls detected.",
        },
        {
          id: "demo-cta-accessibility",
          name: "Primary CTA exposes accessible label",
          status: "failed",
          passed: false,
          category: "secondary",
          summary: "Primary CTA lacks an accessible name.",
          recommendation: "Provide aria-label/aria-labelledby or visible text.",
        },
        {
          id: "demo-signup-modal",
          name: "Signup modal loads required fields",
          status: "passed",
          passed: true,
          category: "secondary",
          summary: "Signup modal opened with required form controls.",
        },
        {
          id: "demo-form-presence",
          name: "Interactive form not detected (optional)",
          status: "info",
          passed: false,
          category: "secondary",
          summary: "No interactive forms detected on landing page.",
        },
      ],
      failedDetails: [
        {
          id: "primary-cta-destination",
          title: "Primary CTA opens expected destination",
          stepSummary:
            "Clicked the first prominent CTA. The UI flashed a loading spinner but no navigation or modal opened within 3 seconds.",
          console: [
            "[info] clicking button[data-test='primary-cta']",
            "[warning] CTA displayed loading state but remained on the same page",
            "[error] expected route change or modal; none detected before timeout",
          ],
          network: [
            "GET https://app.launchsafely.dev/ - 200 (initial)",
            "(CTA click triggered no network activity)",
          ],
          api: [
            "Expected a navigation event to /pricing or contact modal.",
            "Recommendation: verify link target or handler binding.",
          ],
        },
        {
          id: "auth-guard",
          title: "Authenticated page enforces session guard",
          stepSummary:
            "Attempted to visit /dashboard without an authenticated session. The page rendered dashboard content instead of redirecting to /login.",
          console: [
            "[info] navigating to https://app.launchsafely.dev/dashboard",
            "[warning] session guard did not redirect unauthenticated user",
            "[error] found h1 'Dashboard Overview' before authentication",
          ],
          network: [
            "GET https://app.launchsafely.dev/dashboard - 200 (244 ms)",
            "GET https://app.launchsafely.dev/static/js/dashboard.js - 200 (302 ms)",
          ],
          api: [
            "Expected HTTP 302 redirect to /login, but received 200.",
            "Recommendation: enforce auth middleware on dashboard route.",
          ],
        },
        {
          id: "cta-accessibility",
          title: "Primary CTA exposes accessible label",
          stepSummary:
            "Attempted to click the first prominent CTA button. The element scrolled into view but no navigation or modal appeared within 3 seconds.",
          console: [
            "[info] clicking button[data-test='primary-cta']",
            "[warning] element lacks aria-label or accessible name",
            "[error] assistive technology would announce empty label; CTA inaccessible",
          ],
          network: [
            "GET https://app.launchsafely.dev/ - 200 (initial)",
            "(CTA click triggered no network activity)",
          ],
          api: [
            "Expected an aria-label, aria-labelledby, or visible text for assistive tech.",
            "Recommendation: provide accessible name via aria-label, aria-labelledby, or visible text.",
          ],
        },
      ],
    }),
    []
  );

  // Poll backend when a runId is present
  useEffect(() => {
    if (!runId) {
      setUsingDemoFallback(true);
      return;
    }
    let cancelled = false;
    setLiveData(null);
    setProgressPct(0);
    setUsingDemoFallback(false);
    const activeRunId = runId as string;

    async function poll() {
      try {
        const statusData = await getRunStatus({ apiBase, runId: activeRunId });
        if (!cancelled) {
          setProgressPct(Math.max(0, Math.min(100, statusData.progress)));
        }
        if (statusData.state === "done" || statusData.state === "failed") {
          const resultData = await getRunResult({
            apiBase,
            runId: activeRunId,
          });
          if (!cancelled) {
            setLiveData(resultData);
            setLoading(false);
            setProgressPct(100);
            setUsingDemoFallback(Boolean(resultData?.meta?.simulated));
            if (statusData.state === "failed" && !resultData?.meta?.simulated) {
              setStatusNote("Run failed. Showing collected diagnostics.");
            }
          }
          return;
        }
        if (!cancelled) setStatusNote(statusData.progressNote || "Running...");
        pollRef.current = setTimeout(poll, 1500);
      } catch (e) {
        if (!cancelled) {
          if (e instanceof RunApiError) {
            setStatusNote(e.message);
          } else {
            setStatusNote("Unable to reach backend. Showing demo report.");
          }
          setLoading(false);
          setProgressPct(0);
          setUsingDemoFallback(true);
        }
      }
    }

    setLoading(true);
    setStatusNote("Queued...");
    poll();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [runId]);

  if (!show) return null;

  const normalizedTarget =
    demoTargetUrl && demoTargetUrl.trim().length > 0
      ? demoTargetUrl.trim()
      : null;

  const activeInsights = liveData?.summary
    ? {
        coverage: liveData.summary.coverage,
        totals: liveData.summary.totals,
        suiteName: liveData.summary.suiteName,
        tool: liveData.summary.tool,
        triggeredBy: liveData.summary.triggeredBy,
        executedAt: liveData.summary.executedAt,
        duration: liveData.summary.duration,
        tests: liveData.tests || [],
        failedDetails: liveData.failedDetails || [],
      }
    : runInsights;

  const isLiveRun = Boolean(runId) && !usingDemoFallback;
  const hasLiveResult = Boolean(liveData?.summary);
  const showingDemo = !runId || usingDemoFallback;

  const coveragePercent = hasLiveResult
    ? activeInsights.coverage
    : isLiveRun
    ? progressPct
    : activeInsights.coverage;

  const testsToRender = hasLiveResult
    ? activeInsights.tests
    : isLiveRun
    ? []
    : runInsights.tests;

  const sortedTests = testsToRender.slice().sort((a: any, b: any) => {
    const order = (test: any) => {
      const category = test.category || "primary";
      if (test.status === "failed" && category === "primary") return 0;
      if (test.status === "failed") return 1;
      if (test.status === "passed") return 2;
      return 3;
    };
    return order(a) - order(b);
  });

  const scopedFailures: RunTest[] =
    hasLiveResult && Array.isArray(liveData?.tests)
      ? (liveData?.tests as RunTest[]).filter(
          (t) => t.status === "failed" && t.logs
        )
      : [];
  const severityOrder = (test: RunTest) =>
    test.category === "primary" ? 0 : 1;
  const sortedFailureCards = scopedFailures.slice().sort((a, b) => {
    const categoryRank = severityOrder(a) - severityOrder(b);
    if (categoryRank !== 0) return categoryRank;
    return (a.name || "").localeCompare(b.name || "");
  });
  const hasScopedLogs = sortedFailureCards.length > 0;

  const totals = activeInsights.totals;
  const passedCount = totals?.passed ?? 0;
  const failedCount = totals?.failed ?? 0;
  const warningCount = totals?.warnings ?? 0;
  const infoCount = totals?.info ?? 0;
  const totalCount = totals?.total ?? passedCount + failedCount + warningCount;

  const getStatusMeta = (test: any) => {
    const category = test.category || "primary";
    if (test.status === "passed")
      return { label: "PASS", color: palette.success };
    if (test.status === "failed") {
      if (category === "secondary") {
        return { label: "WARN", color: palette.warning };
      }
      return { label: "FAIL", color: palette.danger };
    }
    return { label: "INFO", color: palette.textMuted };
  };

  const donutDenominator = totalCount > 0 ? totalCount : 1;
  const passShare = (passedCount / donutDenominator) * 100 || 0;

  return (
    <section
      style={{
        width: "100%",
        maxWidth: "540px",
        margin: "0 auto",
        padding: "clamp(1.25rem, 5vw, 2rem)",
        background: palette.card,
        border: `1px solid ${palette.border}`,
        borderRadius: "16px",
        boxShadow: palette.shadow,
        color: palette.textPrimary,
        textAlign: "left",
        boxSizing: "border-box",
      }}
    >
      <h2
        style={{
          marginBottom: "1rem",
          color: palette.textPrimary,
          fontSize: "1.25rem",
          fontWeight: 700,
        }}
      >
        {loading && isLiveRun ? "Running Tests" : "Test Run Complete"}
      </h2>

      {/* Demo target originates from Hero.tsx user input for this LaunchSafely preview */}
      {normalizedTarget && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 0.75rem",
            borderRadius: "999px",
            background: "rgba(249, 115, 22, 0.12)",
            border: "1px solid rgba(249, 115, 22, 0.35)",
            color: palette.accentAlt,
            fontSize: "0.85rem",
            marginBottom: "1rem",
          }}
        >
          <span
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: palette.accent,
            }}
          >
            target url
          </span>
          <span style={{ color: palette.textPrimary }}>{normalizedTarget}</span>
        </div>
      )}

      <div
        style={{
          background: palette.cardAlt,
          height: "12px",
          borderRadius: "8px",
          marginBottom: "0.5rem",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.max(0, Math.min(coveragePercent || 0, 100))}%`,
            background: palette.accentGradient,
            borderRadius: "8px",
          }}
        />
      </div>
      <p
        style={{ color: palette.textMuted, marginBottom: "0.75rem" }}
        aria-live="polite"
      >
        {isLiveRun && !hasLiveResult
          ? statusNote
          : `Overall Coverage: ${Math.round(coveragePercent || 0)}%`}
      </p>

      {showingDemo && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 1rem",
            borderRadius: "8px",
            background: fallbackCallout.background,
            border: fallbackCallout.border,
            color: fallbackCallout.color,
            fontSize: "0.9rem",
          }}
        >
          {(() => {
            if (!runId) {
              return "Showing LaunchSafely demo results. Run the demo to preview a sample report.";
            }
            if (liveData?.meta?.simulated) {
              return "LaunchSafely runner is in simulate mode; displaying mock results.";
            }
            return "We couldn't reach the runner, so a mock report is displayed.";
          })()}
        </div>
      )}

      {sortedTests.length > 0 ? (
        <ul style={{ listStyle: "none", padding: 0, marginBottom: "1.5rem" }}>
          {sortedTests.map((test: any) => {
            const { label, color } = getStatusMeta(test);
            return (
              <li
                key={test.name}
                style={{
                  marginBottom: "0.5rem",
                  color,
                  fontFamily: "monospace",
                }}
              >
                [{label}] {test.name}
              </li>
            );
          })}
        </ul>
      ) : isLiveRun ? (
        <div
          style={{
            marginBottom: "1.5rem",
            padding: "0.8rem 1rem",
            borderRadius: "8px",
            background: palette.accentSoft,
            border: `1px solid ${palette.borderSoft}`,
            color: palette.accent,
            fontSize: "0.9rem",
          }}
        >
          We&rsquo;re collecting results&#8230; this list will populate once the
          run completes.
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <button
          style={{
            padding: "0.7rem 1.2rem",
            borderRadius: "8px",
            border: `1px solid ${palette.border}`,
            background: loading ? palette.cardAlt : palette.card,
            color: palette.textPrimary,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
          disabled={loading}
          onClick={() => !loading && setShowReport(true)}
        >
          {loading ? "Preparing Report..." : "See Test Report"}
        </button>
        <button
          style={{
            padding: "0.7rem 1.2rem",
            borderRadius: "8px",
            border: "none",
            fontWeight: 600,
            background: palette.accentGradient,
            color: palette.textOnAccent,
            cursor: "pointer",
          }}
          onClick={() => onHireRequest?.()}
        >
          Hire a Tester
        </button>
      </div>

      {showReport && (
        <Modal
          title="Staging Smoke Suite • Test Report"
          onClose={() => setShowReport(false)}
        >
          <div
            style={{
              maxWidth: "100%",
              overflowX: "hidden",
            }}
          >
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              background: "rgba(56, 189, 248, 0.12)",
              border: "1px solid rgba(56, 189, 248, 0.35)",
              color: palette.accent,
              fontSize: "0.9rem",
            }}
          >
            This preview is an illustrative LaunchSafely report. Content and
            structure is subject to change ahead of full release.
          </div>
          <div
            style={{
              display: "grid",
              gap: "1.5rem",
            }}
          >
            {liveData?.artifacts?.length ? (
              <div
                style={{
                  border: `1px solid ${palette.border}`,
                  borderRadius: "8px",
                  padding: "0.9rem",
                  background: palette.cardAlt,
                }}
              >
                <div
                  style={{
                    color: palette.textSecondary,
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                  }}
                >
                  Artifacts
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                    maxWidth: "100%",
                    wordBreak: "break-word",
                  }}
                >
                  {liveData.artifacts.map((a: any, idx: number) => {
                    const href = a?.url?.startsWith("http")
                      ? a.url
                      : `${apiBase}${a?.url || ""}`;
                    if (a.kind === "trace") {
                      return (
                        <span
                          key={idx}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          <a
                            href={href}
                            style={{
                              color: palette.accent,
                              textDecoration: "underline",
                            }}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Download Trace
                          </a>
                          <span
                            title={
                              "A Playwright trace is an interactive timeline of your run (steps, screenshots, DOM, network & console). Open it at trace.playwright.dev or with: npx playwright show-trace trace.zip"
                            }
                            aria-label="What is this?"
                            style={{
                              marginLeft: 6,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 16,
                              height: 16,
                              borderRadius: 999,
                              border: `1px solid ${palette.border}`,
                              color: palette.textSecondary,
                              fontSize: 11,
                              textDecoration: "none",
                              cursor: "help",
                            }}
                            role="img"
                          >
                            ?
                          </span>
                        </span>
                      );
                    }
                    return (
                      <a
                        key={idx}
                        href={href}
                        style={{
                          color: palette.accent,
                          textDecoration: "underline",
                        }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {a.kind === "screenshot"
                          ? "View Screenshot"
                          : "Artifact"}
                      </a>
                    );
                  })}
                </div>
                {liveData.artifacts.some((a: any) => a.kind === "trace") && (
                  <div
                    style={{
                      color: palette.textMuted,
                      fontSize: "0.85rem",
                      marginTop: "0.5rem",
                      wordBreak: "break-word",
                    }}
                  >
                    Open the trace online:{" "}
                    <a
                      href="https://trace.playwright.dev"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: palette.accent,
                        textDecoration: "underline",
                      }}
                    >
                      trace.playwright.dev
                    </a>
                    <br />
                    Or via CLI:{" "}
                    <code
                      style={{
                        display: "inline-block",
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      npx playwright show-trace trace.zip
                    </code>
                  </div>
                )}
              </div>
            ) : null}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "0.75rem",
                color: palette.textPrimary,
                fontSize: "0.95rem",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              <div>
                <div style={{ color: palette.textMuted, fontSize: "0.8rem" }}>
                  Suite
                </div>
                {activeInsights.suiteName}
              </div>
              <div>
                <div style={{ color: palette.textMuted, fontSize: "0.8rem" }}>
                  Tool
                </div>
                {activeInsights.tool}
              </div>
              <div>
                <div style={{ color: palette.textMuted, fontSize: "0.8rem" }}>
                  Duration
                </div>
                {activeInsights.duration}
              </div>
              <div>
                <div style={{ color: palette.textMuted, fontSize: "0.8rem" }}>
                  Executed
                </div>
                {activeInsights.executedAt}
              </div>
              <div>
                <div style={{ color: palette.textMuted, fontSize: "0.8rem" }}>
                  Triggered By
                </div>
                {activeInsights.triggeredBy}
              </div>
              {normalizedTarget && (
                <div>
                  <div style={{ color: palette.textMuted, fontSize: "0.8rem" }}>
                    Demo Target URL
                  </div>
                  {normalizedTarget}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1.5rem",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "160px",
                  height: "160px",
                  borderRadius: "50%",
                  background: `conic-gradient(${palette.success} 0 ${passShare}%, ${palette.danger} ${passShare}% 100%)`,
                  boxShadow: "0 0 20px rgba(34, 197, 94, 0.25)",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: "20%",
                    background: palette.cardAlt,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: palette.textPrimary,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  {passedCount}/{donutDenominator}
                </div>
              </div>
              <div style={{ flex: "1 1 220px" }}>
                <div
                  style={{
                    marginBottom: "0.5rem",
                    color: palette.textSecondary,
                  }}
                >
                  Test Distribution
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    color: palette.textMuted,
                    fontSize: "0.9rem",
                  }}
                >
                  <div>
                    <div style={{ color: palette.success, fontWeight: 600 }}>
                      {passedCount} Passed
                    </div>
                    <div>Stable user flows confirmed</div>
                  </div>
                  <div>
                    <div style={{ color: palette.danger, fontWeight: 600 }}>
                      {failedCount} Failed
                    </div>
                    <div>Action required before release</div>
                  </div>
                  <div>
                    <div style={{ color: palette.warning, fontWeight: 600 }}>
                      {warningCount} Warnings
                    </div>
                    <div>Optional checks to consider</div>
                  </div>
                </div>
                {infoCount > 0 && (
                  <div
                    style={{
                      marginTop: "0.6rem",
                      color: palette.textMuted,
                      fontSize: "0.85rem",
                    }}
                  >
                    {infoCount} informational checks (not counted toward
                    coverage)
                  </div>
                )}
              </div>
            </div>

            {sortedFailureCards.length > 0 && (
              <div style={{ marginTop: "1.5rem", width: "100%" }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: "0.75rem",
                    color: palette.textPrimary,
                  }}
                >
                  Key Failures
                </div>
                {sortedFailureCards.map((test) => (
                  <FailureCard
                    key={`modal-${test.id || test.name}`}
                    test={test}
                    palette={palette}
                    artifacts={liveData?.artifacts || []}
                    apiBase={apiBase}
                  />
                ))}
              </div>
            )}

            {!hasScopedLogs && (
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: "0.75rem",
                    color: palette.textPrimary,
                  }}
                >
                  Failed Steps
                </div>
                {activeInsights.failedDetails.map((detail: any) => (
                  <div
                    key={detail.id}
                    style={{
                      border: `1px solid ${palette.border}`,
                      borderRadius: "8px",
                      padding: "1rem",
                      marginBottom: "1rem",
                      background: palette.cardAlt,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: "0.4rem",
                        color: palette.danger,
                      }}
                    >
                      {detail.title}
                    </div>
                    <div
                      style={{
                        color: palette.textMuted,
                        fontSize: "0.9rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      {detail.stepSummary}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: "0.75rem",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: palette.textMuted,
                            fontSize: "0.8rem",
                            marginBottom: "0.3rem",
                          }}
                        >
                          Console
                        </div>
                        <div
                          style={{
                            background: palette.cardAlt,
                            borderRadius: "6px",
                            padding: "0.6rem",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            color: palette.textSecondary,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {detail.console.map((line: any) => (
                            <div key={line}>{line}</div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div
                          style={{
                            color: palette.textMuted,
                            fontSize: "0.8rem",
                            marginBottom: "0.3rem",
                          }}
                        >
                          Network
                        </div>
                        <div
                          style={{
                            background: palette.cardAlt,
                            borderRadius: "6px",
                            padding: "0.6rem",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            color: palette.textSecondary,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {detail.network.map((line: any) => (
                            <div key={line}>{line}</div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div
                          style={{
                            color: palette.textMuted,
                            fontSize: "0.8rem",
                            marginBottom: "0.3rem",
                          }}
                        >
                          API Trace
                        </div>
                        <div
                          style={{
                            background: palette.cardAlt,
                            borderRadius: "6px",
                            padding: "0.6rem",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            color: palette.textSecondary,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {detail.api.map((line: any) => (
                            <div key={line}>{line}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

interface FailureCardProps {
  test: RunTest;
  palette: any;
  artifacts: Array<{ kind?: string; url?: string }>;
  apiBase: string;
}

function FailureCard({ test, palette, artifacts, apiBase }: FailureCardProps) {
  if (!test.logs) return null;
  const [copied, setCopied] = useState(false);
  const consoleEntries = test.logs.console || [];
  const networkLogs = test.logs.network || {};
  const navEntries = networkLogs.nav || [];
  const apiEntries = networkLogs.api || [];
  const navMore = networkLogs.navMore;
  const apiMore = networkLogs.apiMore;
  const thirdPartyFold = networkLogs.thirdPartyFold || [];
  const noNetworkActivity = navEntries.length === 0 && apiEntries.length === 0;

  const handleCopy = async () => {
    try {
      const payload = copyFailureAsText(test, artifacts, apiBase);
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = payload;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <article
      style={{
        border: `1px solid ${palette.border}`,
        borderRadius: "12px",
        padding: "1rem",
        marginBottom: "1rem",
        background: palette.card,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.75rem",
        }}
      >
        <h4
          style={{
            margin: 0,
            fontWeight: 600,
            color: palette.danger || "#dc2626",
            lineHeight: 1.2,
          }}
        >
          {test.name}
        </h4>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {test.code && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "999px",
                border: `1px solid ${palette.borderSoft}`,
                color: palette.textSecondary,
                fontSize: "0.7rem",
                padding: "0.2rem 0.55rem",
                textTransform: "lowercase",
              }}
            >
              {test.code}
            </span>
          )}
          <button
            type="button"
            onClick={handleCopy}
            style={{
              fontSize: "0.75rem",
              border: "none",
              background: "transparent",
              color: palette.textSecondary,
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
            }}
            aria-label={`Copy summary for ${test.name}`}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {test.summary && (
        <p
          style={{
            marginTop: "0.4rem",
            color: palette.textMuted,
            fontSize: "0.95rem",
          }}
        >
          {test.summary}
        </p>
      )}

      {consoleEntries.length > 0 && (
        <section style={{ marginTop: "1rem" }}>
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: palette.textSecondary,
            }}
          >
            Console
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "0.4rem 0 0",
            }}
          >
            {consoleEntries.map((entry, idx) => (
              <li
                key={`console-${idx}`}
                style={{
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  color: palette.textPrimary,
                  marginBottom: "0.2rem",
                }}
              >
                [{entry.level}] {entry.msg}
              </li>
            ))}
            {test.logs.consoleMore ? (
              <li style={{ fontSize: "0.85rem", color: palette.textMuted }}>
                +{test.logs.consoleMore} more...
              </li>
            ) : null}
          </ul>
        </section>
      )}

      <section style={{ marginTop: "1rem" }}>
        <div
          style={{
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: palette.textSecondary,
          }}
        >
          Network
        </div>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "0.4rem 0 0",
            fontFamily: "monospace",
            fontSize: "0.9rem",
            color: palette.textPrimary,
          }}
        >
          {navEntries.map((entry, idx) => (
            <li key={`nav-${idx}`}>
              {entry.method} {entry.url} - {entry.status}{" "}
              {idx === 0 ? "(initial)" : `(${entry.ms} ms)`}
            </li>
          ))}
          {navMore ? (
            <li style={{ fontSize: "0.85rem", color: palette.textMuted }}>
              +{navMore} more...
            </li>
          ) : null}
          {apiEntries.map((entry, idx) => (
            <li key={`api-${idx}`}>
              {entry.method} {entry.url} - {entry.status} ({entry.ms} ms)
            </li>
          ))}
          {apiMore ? (
            <li style={{ fontSize: "0.85rem", color: palette.textMuted }}>
              +{apiMore} more...
            </li>
          ) : null}
          {noNetworkActivity && (
            <li
              style={{
                fontStyle: "italic",
                color: palette.textSecondary,
              }}
            >
              (No logged network activity)
            </li>
          )}
        </ul>
        {thirdPartyFold.length > 0 && (
          <details style={{ marginTop: "0.5rem" }}>
            <summary
              style={{
                cursor: "pointer",
                fontSize: "0.75rem",
                color: palette.textSecondary,
              }}
            >
              Third-party (collapsed)
            </summary>
            <ul
              style={{
                listStyle: "none",
                paddingLeft: "1rem",
                marginTop: "0.4rem",
                fontFamily: "monospace",
                fontSize: "0.85rem",
                color: palette.textPrimary,
              }}
            >
              {thirdPartyFold.slice(0, 6).map((entry, idx) => (
                <li key={`third-${idx}`}>
                  {entry.host} - {entry.calls} call
                  {entry.calls === 1 ? "" : "s"}
                  {entry.failures
                    ? `, ${entry.failures} failure${
                        entry.failures === 1 ? "" : "s"
                      }`
                    : ""}
                </li>
              ))}
              {thirdPartyFold.length > 6 ? (
                <li style={{ color: palette.textMuted }}>
                  +{thirdPartyFold.length - 6} more...
                </li>
              ) : null}
            </ul>
          </details>
        )}
      </section>

      {test.apiTrace && (
        <section style={{ marginTop: "1rem" }}>
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: palette.textSecondary,
            }}
          >
            API Trace
          </div>
          <p
            style={{
              marginTop: "0.4rem",
              color: palette.textPrimary,
              fontSize: "0.95rem",
            }}
          >
            {test.apiTrace}
          </p>
        </section>
      )}

      {test.recommendation && (
        <section style={{ marginTop: "0.9rem" }}>
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: palette.textSecondary,
            }}
          >
            Recommendation
          </div>
          <p
            style={{
              marginTop: "0.4rem",
              color: palette.textPrimary,
              fontSize: "0.95rem",
            }}
          >
            {test.recommendation}
          </p>
        </section>
      )}
    </article>
  );
}

function copyFailureAsText(
  test: RunTest,
  artifacts: FailureCardProps["artifacts"],
  apiBase: string
) {
  const logs = test.logs;
  const topConsole =
    (logs?.console || [])
      .slice(0, 3)
      .map((entry) => `[${entry.level}] ${entry.msg}`)
      .join("\n") || "-";

  const navEntries = logs?.network?.nav || [];
  const apiEntries = logs?.network?.api || [];
  const combinedNetwork =
    [...navEntries, ...apiEntries]
      .slice(0, 4)
      .map(
        (entry) =>
          `${entry.method} ${entry.url} -> ${entry.status} (${entry.ms} ms)`
      )
      .join("\n") || "-";

  const formatLink = (url?: string) => {
    if (!url) return "-";
    return url.startsWith("http") ? url : `${apiBase || ""}${url}`;
  };
  const screenshot = artifacts.find((a) => a.kind === "screenshot");
  const trace = artifacts.find((a) => a.kind === "trace");

  return [
    test.name,
    test.summary || "No summary provided.",
    `Code: ${test.code || "-"}`,
    `API Trace: ${test.apiTrace || "-"}`,
    "Console:",
    topConsole,
    "Network:",
    combinedNetwork,
    `Screenshot: ${formatLink(screenshot?.url)}`,
    `Trace: ${formatLink(trace?.url)}`,
  ].join("\n");
}
